/**
 * Job Descriptions Directory — 52 ATS-Friendly, Ready-to-Use Job Descriptions
 * Categorized across 6 core enterprise departments.
 * Includes complete responsibilities, qualifications, benefits, compensation, and copyable templates.
 */

export const JOB_DESCRIPTION_DEPARTMENTS = [
  "All",
  "Engineering & IT",
  "Finance & Accounting",
  "Sales & Business Development",
  "Human Resources & Admin",
  "Marketing & Creative",
  "Operations & Management"
];

export const JOB_DESCRIPTIONS = [
  {
    "title": "Software Developer",
    "slug": "software-developer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹6,00,000 - ₹14,00,000 P.A.",
    "summary": "We are seeking a talented Software Developer to design, develop, and deploy scalable software solutions. You will collaborate with cross-functional teams to build robust microservices and interactive user-facing features.",
    "responsibilities": [
      "Write clean, maintainable, and efficient code following industry best practices and standards.",
      "Design, build, and maintain scalable APIs and microservices architectures.",
      "Collaborate with product managers, UX designers, and QA engineers to ship high-quality features.",
      "Conduct code reviews, implement unit and integration tests, and debug production issues.",
      "Participate in agile sprint ceremonies, backlog grooming, and system architecture discussions.",
      "Monitor application performance, optimize latency, and ensure 99.9% uptime."
    ],
    "requirements": [
      "Bachelor's degree in Computer Science, Information Technology, or relevant engineering field.",
      "2+ years of hands-on software development experience in languages like JavaScript, TypeScript, Python, or Java.",
      "Strong proficiency with relational (PostgreSQL, MySQL) and NoSQL databases (MongoDB, Redis).",
      "Demonstrated understanding of RESTful API design, HTTP protocols, and asynchronous programming.",
      "Familiarity with version control systems (Git) and CI/CD automated deployment pipelines.",
      "Strong analytical problem-solving mindset and eagerness to learn new technologies."
    ],
    "preferred": [
      "Experience with cloud ecosystems such as AWS, Google Cloud, or Azure.",
      "Working knowledge of Docker, Kubernetes, and containerized microservices.",
      "Familiarity with test-driven development (TDD) and automated test suites (Jest, Mocha, Cypress)."
    ],
    "benefits": [
      "Competitive compensation with annual performance appraisal and bonus incentives.",
      "Comprehensive group health insurance covering employee and immediate family.",
      "Flexible hybrid work policy with home office ergonomics allowance.",
      "Annual learning and development stipend for certifications, conferences, and courses."
    ],
    "faqs": [
      {
        "q": "What technical stack should candidates know?",
        "a": "Our core backend is built on Node.js/Python microservices with React on the frontend. We evaluate foundational computer science principles and problem-solving over exact syntax."
      },
      {
        "q": "How does the interview process work?",
        "a": "The hiring process includes an initial HR screening (30 mins), a technical live coding & architecture round (60 mins), and a final culture & managerial discussion."
      },
      {
        "q": "Are remote candidates considered?",
        "a": "Yes, we support hybrid work models across major tech hubs in India (Bengaluru, Pune, Hyderabad, Delhi NCR, Ahmedabad)."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Front End Developer",
    "slug": "front-end-developer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "2 - 4 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹5,50,000 - ₹12,00,000 P.A.",
    "summary": "Looking for an energetic Front End Developer passionate about crafting responsive, pixel-perfect, and ultra-fast web applications with React, Next.js, and modern CSS architectures.",
    "responsibilities": [
      "Translate Figma/Sketch UI/UX design mockups into reusable, performant React/Vue components.",
      "Optimize web applications for maximum speed, scalability, and cross-browser responsiveness.",
      "Collaborate with backend engineers to integrate RESTful and GraphQL APIs seamlessly.",
      "Implement accessibility (a11y) standards, state management (Redux/Zustand), and animations.",
      "Troubleshoot client-side rendering bottlenecks and maintain unit tests using Vitest/Jest."
    ],
    "requirements": [
      "Degree in CS/IT or proven web development portfolio.",
      "Proficiency in modern JavaScript (ES6+), HTML5, CSS3/Tailwind, and React.js ecosystem.",
      "Solid grasp of responsive layouts, CSS Flexbox/Grid, and web performance metrics (Core Web Vitals).",
      "Familiarity with npm/yarn, Vite, Webpack, and Git workflows."
    ],
    "preferred": [
      "Experience with Next.js, SSR, and TypeScript.",
      "Knowledge of WebSockets and micro-frontends."
    ],
    "benefits": [
      "Flexible working hours",
      "MacBook Pro workstation",
      "Health and dental coverage",
      "Paid sick and casual leaves"
    ],
    "faqs": [
      {
        "q": "What UI library is preferred?",
        "a": "We primarily build custom components with Tailwind CSS and Framer Motion."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Full Stack Developer",
    "slug": "full-stack-developer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Senior",
    "experience": "3 - 6 Years",
    "type": "Full-Time",
    "salary": "₹8,00,000 - ₹18,00,000 P.A.",
    "summary": "Seeking a versatile Full Stack Developer capable of owning end-to-end features from database schema design through backend services to rich frontend interfaces.",
    "responsibilities": [
      "Architect and implement full stack features across React/Vue frontend and Node.js/Go/Python backend.",
      "Design efficient database schemas, indexing strategies, and caching layers with Redis.",
      "Build secure authentication protocols (OAuth2, JWT, SSO) and role-based access control.",
      "Deploy and maintain scalable services on cloud infrastructure."
    ],
    "requirements": [
      "3+ years full stack web engineering experience.",
      "Proficiency in React/TypeScript frontend and Node.js/Python backend.",
      "Strong SQL and ORM experience (PostgreSQL, Prisma, TypeORM).",
      "Deep understanding of software architecture patterns and web security principles."
    ],
    "preferred": [
      "AWS Lambda/Serverless or Docker deployment experience.",
      "GraphQL API exposure."
    ],
    "benefits": [
      "ESOP equity options",
      "Annual retreat",
      "Unlimited learning resources",
      "Comprehensive health coverage"
    ],
    "faqs": [
      {
        "q": "What is the team size?",
        "a": "Engineering pods typically consist of 5-8 engineers including product and design."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Java Developer",
    "slug": "java-developer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "3 - 5 Years",
    "type": "Full-Time",
    "salary": "₹7,00,000 - ₹15,00,000 P.A.",
    "summary": "We are hiring an experienced Java Developer to develop enterprise-grade, high-throughput microservices using Spring Boot, Hibernate, and Apache Kafka.",
    "responsibilities": [
      "Develop mission-critical banking and HRMS billing engines in Java 17+ and Spring Boot.",
      "Build asynchronous event-driven messaging pipelines using Kafka and RabbitMQ.",
      "Write comprehensive automated unit and integration tests with JUnit and Mockito.",
      "Analyze and resolve performance bottlenecks in distributed JVM architectures."
    ],
    "requirements": [
      "Strong proficiency in Core Java, multithreading, concurrency, and OOP principles.",
      "Extensive experience with Spring Framework (Spring Boot, Spring Security, Spring Data).",
      "Experience with relational databases (MySQL, Oracle, PostgreSQL).",
      "Familiarity with Maven, Gradle, Docker, and CI/CD pipelines."
    ],
    "preferred": [
      "Experience in fintech, payments, or cloud-native microservices.",
      "Kubernetes deployment skills."
    ],
    "benefits": [
      "Health coverage for dependents",
      "Annual bonus",
      "Wellness subsidies",
      "Flexible PTO"
    ],
    "faqs": [
      {
        "q": "Which Java version is used?",
        "a": "We run modern Java 17 and Java 21 LTS in production."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Android Developer",
    "slug": "android-developer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹6,00,000 - ₹14,00,000 P.A.",
    "summary": "Join our mobile team to build next-generation native Android applications for millions of workforce users using Kotlin, Jetpack Compose, and modern MVVM/MVI architectures.",
    "responsibilities": [
      "Build intuitive, high-performance Android mobile apps using Kotlin and Jetpack Compose.",
      "Integrate RESTful APIs, biometric authentication (fingerprint/face unlock), and offline caching with Room.",
      "Ensure seamless GPS geolocation tracking and push notifications (FCM).",
      "Maintain code quality through unit testing and automated release pipelines to Google Play Store."
    ],
    "requirements": [
      "2+ years experience in Android app development with Kotlin.",
      "Strong knowledge of Android SDK, Coroutines, Flow, and Jetpack components.",
      "Experience with offline data storage, threading, and performance tuning.",
      "Published apps on Google Play Store."
    ],
    "preferred": [
      "Experience with Kotlin Multiplatform (KMP) or Flutter.",
      "CI/CD using GitHub Actions or Bitrise."
    ],
    "benefits": [
      "Device testing allowance",
      "Hybrid work flexibility",
      "Medical insurance",
      "Gym membership"
    ],
    "faqs": [
      {
        "q": "Is Jetpack Compose mandatory?",
        "a": "Yes, our new screens are exclusively written in Jetpack Compose."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "DevOps Engineer",
    "slug": "devops-engineer-job-description",
    "department": "Engineering & IT",
    "level": "Senior",
    "experience": "4 - 7 Years",
    "type": "Full-Time",
    "salary": "₹10,00,000 - ₹22,00,000 P.A.",
    "summary": "Looking for an experienced DevOps Engineer to build, automate, and scale our cloud infrastructure, CI/CD pipelines, and observability monitoring systems.",
    "responsibilities": [
      "Manage AWS/GCP cloud infrastructure using Infrastructure as Code (Terraform, CloudFormation).",
      "Deploy, monitor, and scale Kubernetes clusters (EKS/GKE) and microservices.",
      "Design robust CI/CD pipelines with GitHub Actions, GitLab CI, or Jenkins.",
      "Implement centralized logging, metrics, and tracing using Prometheus, Grafana, and ELK."
    ],
    "requirements": [
      "4+ years of DevOps / Cloud Infrastructure experience.",
      "Deep expertise with Linux administration, shell scripting, and Python/Go automation.",
      "Hands-on production experience with Docker and Kubernetes orchestration.",
      "Strong understanding of VPC networking, DNS, SSL/TLS, and IAM security controls."
    ],
    "preferred": [
      "AWS Certified Solutions Architect / CKA certified.",
      "Experience with SOC2 and ISO 27001 compliance."
    ],
    "benefits": [
      "Competitive equity",
      "WFH setup allowance",
      "Premium health insurance",
      "Flexible working hours"
    ],
    "faqs": [
      {
        "q": "What is the on-call policy?",
        "a": "We have a balanced rotational on-call schedule with comp-off and bonuses."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Data Analyst",
    "slug": "data-analyst-job-description",
    "department": "Engineering & IT",
    "level": "Entry-Mid",
    "experience": "1 - 4 Years",
    "type": "Full-Time",
    "salary": "₹4,50,000 - ₹9,50,000 P.A.",
    "summary": "Seeking a Data Analyst to transform complex datasets into actionable business intelligence, KPI dashboards, and data-driven insights across product, sales, and operations.",
    "responsibilities": [
      "Extract, clean, and analyze operational and product data using SQL, Python, and Excel.",
      "Build and maintain interactive dashboards in Tableau, Power BI, or Looker.",
      "Collaborate with business stakeholders to define KPIs and monitor funnel conversion metrics.",
      "Perform cohort analysis, churn prediction, and revenue forecasting."
    ],
    "requirements": [
      "Strong proficiency in SQL queries, window functions, and joins.",
      "Hands-on experience with visualization tools (Power BI, Tableau) and Excel.",
      "Familiarity with Python (Pandas, NumPy) for data manipulation.",
      "Degree in Statistics, Mathematics, Economics, or Computer Science."
    ],
    "preferred": [
      "Experience with cloud data warehouses (Snowflake, BigQuery).",
      "Knowledge of basic statistical modeling."
    ],
    "benefits": [
      "Health insurance",
      "Annual appraisals",
      "Quarterly team outings",
      "Training grants"
    ],
    "faqs": [
      {
        "q": "What BI tools do you use?",
        "a": "We primarily utilize Power BI and PostgreSQL data pipelines."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Business Analyst",
    "slug": "business-analyst-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "3 - 6 Years",
    "type": "Full-Time",
    "salary": "₹6,50,000 - ₹13,00,000 P.A.",
    "summary": "Act as the bridge between business stakeholders and technical teams to gather requirements, analyze operational workflows, and define clear functional specifications (BRDs/PRDs).",
    "responsibilities": [
      "Conduct stakeholder interviews and requirement gathering workshops.",
      "Document Business Requirement Documents (BRDs), user stories, and acceptance criteria in Jira.",
      "Map current and future state business processes using flowcharting tools (Visio, Lucidchart).",
      "Participate in user acceptance testing (UAT) and validate deliverable readiness."
    ],
    "requirements": [
      "3+ years experience as a Business Analyst in SaaS, IT services, or BFSI.",
      "Exceptional requirement documentation, wireframing, and communication skills.",
      "Experience with Agile/Scrum frameworks, Jira, and Confluence.",
      "Basic SQL knowledge for data validation and workflow verification."
    ],
    "preferred": [
      "CBAP or PMI-PBA certification.",
      "Prior HR tech or ERP experience."
    ],
    "benefits": [
      "Family medical insurance",
      "Learning budget",
      "Performance incentives",
      "Hybrid schedule"
    ],
    "faqs": [
      {
        "q": "Will I interact directly with clients?",
        "a": "Yes, you will work closely with client executives and internal engineering teams."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Network Engineer",
    "slug": "network-engineer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time",
    "salary": "₹4,50,000 - ₹9,00,000 P.A.",
    "summary": "Manage, maintain, and secure our enterprise corporate network infrastructure including routers, switches, firewalls, VPNs, and wireless systems.",
    "responsibilities": [
      "Configure and maintain network devices (Cisco, Fortinet, Juniper switches & firewalls).",
      "Monitor network traffic, bandwidth utilization, and latency to ensure high availability.",
      "Implement cybersecurity best practices, VPN tunneling, and VLAN segmentation.",
      "Troubleshoot hardware, LAN/WAN, and VoIP connectivity issues."
    ],
    "requirements": [
      "CCNA/CCNP or equivalent networking certification.",
      "Hands-on experience with routing protocols (BGP, OSPF), TCP/IP, and firewall policies.",
      "Understanding of DNS, DHCP, NAT, and wireless controller management.",
      "Bachelor's degree or diploma in IT/Electronics."
    ],
    "preferred": [
      "Cloud networking experience (AWS VPC, Direct Connect).",
      "Knowledge of SD-WAN."
    ],
    "benefits": [
      "Comprehensive medical cover",
      "Provident fund",
      "Paid sick leave",
      "Overtime allowance"
    ],
    "faqs": [
      {
        "q": "Is shift work required?",
        "a": "This is a general day-shift role with occasional scheduled weekend maintenance."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "IT Operations Manager",
    "slug": "it-operations-manager-job-description",
    "department": "Engineering & IT",
    "level": "Manager",
    "experience": "6 - 10 Years",
    "type": "Full-Time",
    "salary": "₹12,00,000 - ₹24,00,000 P.A.",
    "summary": "Oversee overall IT operations, service desk systems, hardware procurement, software licensing, and enterprise information security protocols.",
    "responsibilities": [
      "Lead the IT helpdesk team and establish SLA metrics for ticket resolution.",
      "Manage IT asset lifecycles, laptop provisioning, and MDM solutions (Jamf, Intune).",
      "Enforce data security, zero-trust access, antivirus policies, and compliance audits.",
      "Oversee IT budget, vendor negotiations, and SaaS software subscriptions."
    ],
    "requirements": [
      "6+ years experience in enterprise IT operations with 2+ years leading a team.",
      "Deep familiarity with Active Directory/Okta/Google Workspace admin and MDM tools.",
      "Strong understanding of ITIL frameworks, disaster recovery, and SOC2 compliance.",
      "Excellent vendor management and contract negotiation skills."
    ],
    "preferred": [
      "ITIL or CISSP certification.",
      "Experience managing 500+ employee infrastructure."
    ],
    "benefits": [
      "Executive health plan",
      "Performance bonus",
      "Car allowance / fuel allowance",
      "ESOP options"
    ],
    "faqs": [
      {
        "q": "How many office locations are covered?",
        "a": "You will oversee IT for our headquarters and remote workforce nationwide."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Accountant",
    "slug": "accountant-job-description",
    "department": "Finance & Accounting",
    "level": "Mid-Level",
    "experience": "2 - 4 Years",
    "type": "Full-Time",
    "salary": "₹3,50,000 - ₹7,00,000 P.A.",
    "summary": "Responsible for managing day-to-day accounting entries, bank reconciliations, statutory GST/TDS filings, and preparing monthly financial statements in compliance with Indian Accounting Standards.",
    "responsibilities": [
      "Post daily journal entries, general ledger maintenance, and accounts payable/receivable.",
      "Perform monthly bank reconciliations and inter-company transactions.",
      "Calculate and file statutory compliance dues including GST (GSTR-1, GSTR-3B), TDS, and PT.",
      "Prepare monthly trial balance, profit & loss statement, and balance sheet schedules.",
      "Assist external auditors during statutory and tax audits."
    ],
    "requirements": [
      "B.Com or M.Com degree; CA Inter or semi-qualified is a strong advantage.",
      "2+ years experience in corporate accounting or accounting firm.",
      "Proficiency in Tally Prime, Zoho Books, or SAP ERP and advanced MS Excel (VLOOKUP, Pivot Tables).",
      "Solid understanding of GST regulations, TDS rates, and Indian accounting principles."
    ],
    "preferred": [
      "Experience handling multi-entity accounts.",
      "Familiarity with MCA filings and ROC."
    ],
    "benefits": [
      "Provident Fund & Gratuity",
      "Health insurance",
      "Annual bonus",
      "Paid leaves"
    ],
    "faqs": [
      {
        "q": "Which accounting software do we use?",
        "a": "We operate on cloud-based ERP and Tally Prime."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Senior Accountant",
    "slug": "senior-accountant-job-description",
    "department": "Finance & Accounting",
    "level": "Senior",
    "experience": "4 - 8 Years",
    "type": "Full-Time",
    "salary": "₹6,00,000 - ₹12,00,000 P.A.",
    "summary": "Oversee the general ledger, audit preparations, tax planning, cash flow forecasting, and supervise junior accounting staff to maintain immaculate financial hygiene.",
    "responsibilities": [
      "Review monthly balance sheet reconciliations and ensure accurate revenue recognition.",
      "Supervise AP/AR cycles, vendor payments, and credit control processes.",
      "Lead annual statutory audit, internal audits, and tax assessments with auditors.",
      "Prepare cash flow statements, MIS reports, and variance analyses for leadership.",
      "Ensure 100% statutory adherence to Advance Tax, GST annual returns, and transfer pricing."
    ],
    "requirements": [
      "M.Com, CA Inter, or Chartered Accountant (CA) qualification.",
      "4+ years of core accounting experience in mid-to-large enterprises.",
      "Comprehensive knowledge of Ind AS, Direct & Indirect Tax laws, and Companies Act.",
      "Strong analytical, organizational, and team management capabilities."
    ],
    "preferred": [
      "Prior experience in SaaS or product companies.",
      "Experience with SAP or Oracle NetSuite."
    ],
    "benefits": [
      "Comprehensive medical policy",
      "Performance bonus",
      "PF and Gratuity",
      "Professional membership reimbursement"
    ],
    "faqs": [
      {
        "q": "Does this role report to the CFO?",
        "a": "Yes, reports directly to the Finance Director / CFO."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Junior Accountant",
    "slug": "junior-accountant-job-description",
    "department": "Finance & Accounting",
    "level": "Entry-Level",
    "experience": "0 - 2 Years",
    "type": "Full-Time",
    "salary": "₹2,50,000 - ₹4,00,000 P.A.",
    "summary": "Ideal entry-level opportunity for a diligent accounting graduate to assist with bookkeeping, invoice processing, voucher entry, and basic bank reconciliations.",
    "responsibilities": [
      "Enter purchase, sales, and expense vouchers into accounting software.",
      "Match vendor purchase orders with incoming invoices and verify tax invoices.",
      "Maintain physical and digital filing of accounting vouchers and receipts.",
      "Assist seniors with routine bank reconciliations and petty cash management."
    ],
    "requirements": [
      "Bachelor's degree in Commerce (B.Com).",
      "Familiarity with basic accounting principles (debits, credits, ledger postings).",
      "Working knowledge of Tally and Microsoft Excel.",
      "Keen attention to detail and eagerness to learn corporate finance."
    ],
    "preferred": [
      "Tally certification or basic GST internship experience."
    ],
    "benefits": [
      "Mentorship from senior CAs",
      "PF & medical coverage",
      "Annual performance review"
    ],
    "faqs": [
      {
        "q": "Are freshers eligible?",
        "a": "Yes, fresh B.Com graduates with good Excel skills are welcome."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Accounting Assistant",
    "slug": "accounting-assistant-job-description",
    "department": "Finance & Accounting",
    "level": "Entry-Mid",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹2,80,000 - ₹4,80,000 P.A.",
    "summary": "Provide operational support to the finance department by processing disbursements, updating billing records, and handling vendor inquiries.",
    "responsibilities": [
      "Process vendor payments, expense reimbursements, and client invoice generation.",
      "Follow up with customers on pending dues and update aging receivables reports.",
      "Maintain petty cash registers and reconcile daily receipts.",
      "Assist in monthly GST reconciliation (GSTR-2B vs Books)."
    ],
    "requirements": [
      "B.Com degree with 1+ year practical bookkeeping experience.",
      "Hands-on skills in Tally and MS Excel.",
      "Strong numeracy and data verification accuracy.",
      "Clear written and verbal communication."
    ],
    "preferred": [
      "Familiarity with e-invoicing and e-way bill generation."
    ],
    "benefits": [
      "Health insurance",
      "Standard paid leaves",
      "Skill development support"
    ],
    "faqs": [
      {
        "q": "What is the working schedule?",
        "a": "Monday to Friday, 9:30 AM to 6:30 PM."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Accounting Manager",
    "slug": "accounting-manager-job-description",
    "department": "Finance & Accounting",
    "level": "Manager",
    "experience": "7 - 12 Years",
    "type": "Full-Time",
    "salary": "₹14,00,000 - ₹26,00,000 P.A.",
    "summary": "Direct all corporate accounting activities, establish internal financial controls, formulate budgeting plans, and deliver monthly financial packages to the executive team.",
    "responsibilities": [
      "Lead and mentor a team of 6-10 accountants, billing specialists, and financial analysts.",
      "Establish and enforce internal controls, accounting SOPs, and compliance frameworks.",
      "Manage working capital, treasury, and short-term investment strategies.",
      "Present monthly financial dashboards and variance analyses to the leadership team.",
      "Oversee statutory filings, transfer pricing documentation, and corporate tax returns."
    ],
    "requirements": [
      "Chartered Accountant (CA) or MBA in Finance.",
      "7+ years post-qualification experience with at least 3 years in a managerial role.",
      "Expert understanding of Indian corporate tax, GST, transfer pricing, and ROC compliance.",
      "Strategic leadership and stakeholder presentation skills."
    ],
    "preferred": [
      "Experience managing IPO readiness or PE/VC audit compliance."
    ],
    "benefits": [
      "Executive health plan",
      "Annual management incentive",
      "ESOP equity",
      "Car allowance"
    ],
    "faqs": [
      {
        "q": "What is the team hierarchy?",
        "a": "You will lead the entire accounting division under the CFO."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Financial Analyst",
    "slug": "financial-analyst-job-description",
    "department": "Finance & Accounting",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time",
    "salary": "₹6,50,000 - ₹13,50,000 P.A.",
    "summary": "Conduct quantitative financial modeling, unit economics analysis, annual budgeting, and deliver actionable insights to drive strategic business growth.",
    "responsibilities": [
      "Build dynamic 3-statement financial models, DCF valuations, and sensitivity scenarios.",
      "Perform monthly actuals vs budget variance analysis across departmental P&Ls.",
      "Track SaaS metrics: ARR, MRR, CAC, LTV, Net Retention Rate, and churn.",
      "Prepare board presentation decks and investor reporting packages."
    ],
    "requirements": [
      "Bachelor's degree in Finance, Economics, or Commerce; CFA or MBA is advantageous.",
      "2+ years experience in FP&A, investment banking, or financial consulting.",
      "Advanced financial modeling skills in Excel and Power BI / Tableau.",
      "Solid grasp of corporate finance principles and unit economics."
    ],
    "preferred": [
      "CFA Level 1 or 2 cleared.",
      "Experience with SQL and Looker."
    ],
    "benefits": [
      "Health coverage",
      "Annual performance bonus",
      "Career progression track",
      "Hybrid flexibility"
    ],
    "faqs": [
      {
        "q": "Will I present to executives?",
        "a": "Yes, you will regularly present financial insights to BU heads."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Financial Manager",
    "slug": "financial-manager-job-description",
    "department": "Finance & Accounting",
    "level": "Manager",
    "experience": "6 - 10 Years",
    "type": "Full-Time",
    "salary": "₹12,00,000 - ₹22,00,000 P.A.",
    "summary": "Oversee organizational financial health, capital allocation, banking relationships, risk mitigation strategies, and multi-year forecasting.",
    "responsibilities": [
      "Direct financial planning and analysis (FP&A) and long-term corporate forecasting.",
      "Manage banking partnerships, credit facilities, working capital lines, and forex hedging.",
      "Evaluate capital expenditure (CapEx) proposals and M&A opportunities.",
      "Formulate tax optimization strategies and maintain relationships with rating agencies."
    ],
    "requirements": [
      "Qualified Chartered Accountant (CA) or MBA Finance from a reputed institute.",
      "6+ years experience in corporate finance, treasury, or FP&A.",
      "Strong knowledge of banking instruments, letters of credit, and treasury management.",
      "Proven track record of driving margin expansion and cost optimization."
    ],
    "preferred": [
      "Experience in fast-scaling tech companies.",
      "Knowledge of international taxation."
    ],
    "benefits": [
      "Health and life insurance",
      "Performance bonus",
      "Leadership coaching",
      "Flexible hybrid schedule"
    ],
    "faqs": [
      {
        "q": "Does this role involve fundraising?",
        "a": "Yes, you will assist the CFO in equity and debt fundraising rounds."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Credit Manager",
    "slug": "credit-manager-job-description",
    "department": "Finance & Accounting",
    "level": "Mid-Senior",
    "experience": "4 - 8 Years",
    "type": "Full-Time",
    "salary": "₹7,00,000 - ₹14,00,000 P.A.",
    "summary": "Evaluate customer creditworthiness, establish enterprise credit limits, enforce receivables collections, and minimize bad debt exposure.",
    "responsibilities": [
      "Formulate corporate credit policies, risk assessment scorecards, and credit terms.",
      "Assess new client financials, bank statements, and credit bureau reports (CIBIL/Dun & Bradstreet).",
      "Monitor aging accounts receivable and drive dispute resolution with key accounts.",
      "Coordinate with legal teams for bad debt recovery proceedings under Section 138 / IBC."
    ],
    "requirements": [
      "Degree in Commerce, Finance, or Banking.",
      "4+ years credit underwriting or credit control experience in B2B / NBFC sectors.",
      "Strong understanding of financial ratios, cash flow health, and credit risk mitigation.",
      "Firm yet professional negotiation and debt collection acumen."
    ],
    "preferred": [
      "Experience in enterprise SaaS credit cycles.",
      "Legal recovery process knowledge."
    ],
    "benefits": [
      "Medical coverage",
      "Performance incentives",
      "Gratuity & PF",
      "Travel reimbursement"
    ],
    "faqs": [
      {
        "q": "What is the typical credit portfolio size?",
        "a": "You will manage a portfolio spanning ₹50Cr+ in annual receivables."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Sales Executive",
    "slug": "sales-executive-job-description",
    "department": "Sales & Business Development",
    "level": "Entry-Mid",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹3,50,000 - ₹6,50,000 + Attractive Incentives",
    "summary": "Driven Sales Executive responsible for prospecting new business, conducting live product demonstrations, pitching HRMS/ERP software solutions, and closing deals.",
    "responsibilities": [
      "Prospect and qualify inbound and outbound leads through cold outreach and email campaigns.",
      "Conduct engaging online and in-person product demonstrations highlighting customer ROI.",
      "Prepare custom commercial proposals, negotiate contract terms, and close sales deals.",
      "Maintain active pipeline status and sales activities in CRM (Salesforce / HubSpot).",
      "Achieve and exceed monthly and quarterly revenue targets."
    ],
    "requirements": [
      "1+ years experience in B2B sales or technology solution selling.",
      "Outstanding presentation, persuasive communication, and closing skills.",
      "Familiarity with CRM platforms and consultative sales methodologies.",
      "Tenacious attitude with a competitive drive to exceed sales quotas."
    ],
    "preferred": [
      "Experience selling HR, payroll, or SaaS solutions in India."
    ],
    "benefits": [
      "Uncapped monthly sales commissions",
      "Travel & mobile allowance",
      "Health insurance",
      "Fast-track promotions"
    ],
    "faqs": [
      {
        "q": "What is the incentive structure?",
        "a": "We offer uncapped monthly incentives with accelerators for 100%+ quota attainment."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Sales Manager",
    "slug": "sales-manager-job-description",
    "department": "Sales & Business Development",
    "level": "Manager",
    "experience": "5 - 9 Years",
    "type": "Full-Time",
    "salary": "₹10,00,000 - ₹20,00,000 + Performance Bonus",
    "summary": "Lead and inspire our regional enterprise sales team, build scalable sales pipelines, coach account executives, and drive revenue acceleration.",
    "responsibilities": [
      "Recruit, train, and coach a high-performing team of 6-10 Sales Executives.",
      "Develop regional sales strategies, territory mapping, and target quotas.",
      "Participate in high-stakes enterprise negotiations and close six-figure ARR accounts.",
      "Track sales velocity, conversion ratios, and pipeline health using CRM dashboards."
    ],
    "requirements": [
      "5+ years of direct B2B sales experience with 2+ years managing a sales team.",
      "Consistent track record of achieving team quotas in enterprise software.",
      "Expertise in consultative selling, deal structuring, and contract negotiations.",
      "Strong leadership presence and data-driven pipeline coaching."
    ],
    "preferred": [
      "Established enterprise network in manufacturing, IT, or retail verticals."
    ],
    "benefits": [
      "Lucrative quarterly executive bonuses",
      "Comprehensive family insurance",
      "Corporate credit card",
      "Company travel perks"
    ],
    "faqs": [
      {
        "q": "What territory will I manage?",
        "a": "Roles are divided into West, North, and South India enterprise corridors."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Business Development Executive",
    "slug": "business-development-executive-job-description",
    "department": "Sales & Business Development",
    "level": "Entry-Level",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹3,00,000 - ₹5,50,000 + Incentives",
    "summary": "Generate high-quality sales qualified leads (SQLs) by identifying target prospects, executing cold outreach across LinkedIn and phone, and booking discovery meetings.",
    "responsibilities": [
      "Identify key decision-makers (CHROs, CFOs, Managing Directors) across target accounts.",
      "Execute multi-touch outreach sequences via phone, LinkedIn Sales Navigator, and email.",
      "Conduct discovery calls to uncover customer pain points and qualify budget, authority, need, and timeline (BANT).",
      "Schedule product demonstrations for senior Account Executives and log notes in CRM."
    ],
    "requirements": [
      "Bachelor's degree in any discipline.",
      "Exceptional phone presence, verbal articulation, and objection handling skills.",
      "Comfortable with high-volume outbound outreach (50+ calls/day).",
      "Goal-oriented mindset and enthusiasm for technology sales."
    ],
    "preferred": [
      "Experience using LinkedIn Sales Navigator, Apollo.io, or HubSpot."
    ],
    "benefits": [
      "Monthly lead generation incentives",
      "Mentorship from top sales leaders",
      "Career progression to Account Executive in 12 months"
    ],
    "faqs": [
      {
        "q": "Is there a structured training program?",
        "a": "Yes, every BDE undergoes a comprehensive 4-week sales academy training."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Business Development Manager",
    "slug": "business-development-manager-job-description",
    "department": "Sales & Business Development",
    "level": "Mid-Senior",
    "experience": "4 - 8 Years",
    "type": "Full-Time",
    "salary": "₹8,00,000 - ₹16,00,000 P.A.",
    "summary": "Identify strategic growth opportunities, forge B2B partnerships with system integrators and HR consultants, and drive market expansion in new industry sectors.",
    "responsibilities": [
      "Formulate market penetration plans for new industry verticals and geographic tiers.",
      "Establish referral partnerships with CA firms, HR consultancies, and IT channel partners.",
      "Lead strategic corporate pitches and respond to government/enterprise RFPs and tenders.",
      "Collaborate with marketing teams on event sponsorships, webinars, and field campaigns."
    ],
    "requirements": [
      "4+ years experience in business development, alliances, or strategic partnerships.",
      "Demonstrated ability to close complex enterprise contracts and channel partnerships.",
      "Strong analytical ability to identify untapped market segments and commercial models.",
      "MBA from a recognized institute preferred."
    ],
    "preferred": [
      "Existing network of HR consultants and payroll service providers in India."
    ],
    "benefits": [
      "Health insurance",
      "Annual performance bonus",
      "Travel allowance",
      "Hybrid work flexibility"
    ],
    "faqs": [
      {
        "q": "What is the primary KPI?",
        "a": "New pipeline sourced, strategic partnerships signed, and partner-influenced revenue."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Account Manager",
    "slug": "account-manager-job-description",
    "department": "Sales & Business Development",
    "level": "Mid-Level",
    "experience": "3 - 6 Years",
    "type": "Full-Time",
    "salary": "₹6,00,000 - ₹12,00,000 P.A.",
    "summary": "Manage relationships with existing corporate accounts, drive customer satisfaction, handle contract renewals, and identify upselling/cross-selling opportunities.",
    "responsibilities": [
      "Serve as the primary strategic point of contact for key client accounts post-onboarding.",
      "Conduct quarterly business reviews (QBRs) to showcase software adoption and value realized.",
      "Identify opportunities to upsell additional software modules (Biometric devices, Payroll, CRM).",
      "Manage annual contract renewals, minimize revenue churn, and ensure high customer NPS."
    ],
    "requirements": [
      "3+ years in account management, customer success, or relationship management in B2B SaaS.",
      "Strong interpersonal skills with ability to consult senior HR & Finance leaders.",
      "Data-driven mindset to analyze product usage metrics and proactively identify at-risk clients.",
      "Effective negotiation and conflict resolution skills."
    ],
    "preferred": [
      "Experience managing 50+ enterprise accounts simultaneously."
    ],
    "benefits": [
      "Renewal and upsell commissions",
      "Health insurance",
      "Learning stipend",
      "Work-from-anywhere days"
    ],
    "faqs": [
      {
        "q": "What is the average portfolio size?",
        "a": "Each Account Manager oversees 40-60 key corporate accounts."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Relationship Manager",
    "slug": "relationship-manager-job-description",
    "department": "Sales & Business Development",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time",
    "salary": "₹5,00,000 - ₹10,00,000 P.A.",
    "summary": "Foster long-term relationships with high-value enterprise clients, ensuring top-tier service delivery, rapid issue escalation, and ongoing client retention.",
    "responsibilities": [
      "Maintain deep, proactive relationships with key client executives and HR leaders.",
      "Coordinate with implementation and technical support teams to resolve complex escalations.",
      "Facilitate product feedback loops to product managers for roadmap prioritization.",
      "Organize user community events, training workshops, and client appreciation sessions."
    ],
    "requirements": [
      "2+ years experience in customer relationship management, corporate banking, or SaaS.",
      "High emotional intelligence, polished communication, and professional demeanor.",
      "Strong organizational skills and ability to manage multiple stakeholder expectations.",
      "Graduate or Post-Graduate in Business, Communications, or Management."
    ],
    "preferred": [
      "Familiarity with HR tech or banking relationship management."
    ],
    "benefits": [
      "Health coverage",
      "Annual bonus",
      "Mobile & communication reimbursement",
      "Team outings"
    ],
    "faqs": [
      {
        "q": "Is travel required?",
        "a": "Occasional local travel for client meetings and QBRs within the city."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "HR Executive",
    "slug": "hr-executive-job-description",
    "department": "Human Resources & Admin",
    "level": "Entry-Mid",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹3,00,000 - ₹5,50,000 P.A.",
    "summary": "Support day-to-day HR operations including talent sourcing, candidate screening, employee onboarding documentation, attendance tracking, and employee engagement activities.",
    "responsibilities": [
      "Source candidate profiles across job boards (Naukri, LinkedIn) and coordinate interview rounds.",
      "Execute end-to-end new hire onboarding, document verification, and background checks.",
      "Maintain employee personnel files and update HRMS database records accurately.",
      "Track monthly attendance, leaves, and biometric logs for payroll processing inputs.",
      "Organize employee engagement games, festival celebrations, and wellness events."
    ],
    "requirements": [
      "Bachelor's degree or MBA in Human Resources.",
      "1+ years experience in generalist HR or talent acquisition.",
      "Good understanding of Indian labor compliance basics (PF, ESI, leaves).",
      "Warm interpersonal skills and proficient in MS Office and HRMS software."
    ],
    "preferred": [
      "Experience using an automated HRMS portal."
    ],
    "benefits": [
      "Health insurance",
      "Annual appraisals",
      "Birthday & work anniversary celebrations",
      "Professional development"
    ],
    "faqs": [
      {
        "q": "What is the onboarding volume?",
        "a": "Typically 10-25 new hires per month across tech and business teams."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "HR Generalist",
    "slug": "hr-generalist-job-description",
    "department": "Human Resources & Admin",
    "level": "Mid-Level",
    "experience": "3 - 6 Years",
    "type": "Full-Time",
    "salary": "₹5,00,000 - ₹9,50,000 P.A.",
    "summary": "Manage core HR functions including employee relations, performance appraisal cycles, statutory labor compliance, policy implementation, and exit management.",
    "responsibilities": [
      "Oversee end-to-end employee lifecycle from onboarding through retention and offboarding.",
      "Implement and interpret company HR policies ensuring compliance with labor laws.",
      "Facilitate bi-annual performance appraisal cycles and OKR tracking across teams.",
      "Handle employee grievances, conduct workplace investigations, and mediate disputes.",
      "Supervise exit interviews, calculate full & final (F&F) settlements, and issue relieving letters."
    ],
    "requirements": [
      "MBA in HR or related field.",
      "3+ years experience as an HR Generalist in a fast-paced organization.",
      "In-depth knowledge of Indian labor acts (Factories Act, POSH, Payment of Gratuity, Bonus Act).",
      "Empathetic communication and proven dispute resolution capability."
    ],
    "preferred": [
      "SHRM or HRCI certification.",
      "Experience handling POSH internal committee procedures."
    ],
    "benefits": [
      "Comprehensive health plan",
      "Annual bonus",
      "Gym subsidy",
      "Flexible work hours"
    ],
    "faqs": [
      {
        "q": "How many employees will I support?",
        "a": "You will act as the dedicated HR partner for 150-250 employees."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "HR Manager",
    "slug": "hr-manager-job-description",
    "department": "Human Resources & Admin",
    "level": "Manager",
    "experience": "6 - 10 Years",
    "type": "Full-Time",
    "salary": "₹10,00,000 - ₹20,00,000 P.A.",
    "summary": "Lead the human resources department, design organizational culture strategies, oversee talent acquisition, total rewards, statutory compliance, and leadership development.",
    "responsibilities": [
      "Develop and execute strategic HR plans aligned with organizational growth objectives.",
      "Lead a team of recruiters, HR generalists, and administrative personnel.",
      "Design competitive compensation & benefits frameworks (ESOPs, medical, bonuses).",
      "Ensure 100% compliance with central and state labor regulations and statutory audits.",
      "Direct leadership succession planning, talent retention strategies, and employer branding."
    ],
    "requirements": [
      "Post Graduate / MBA in Human Resources from a premier business school.",
      "6+ years experience in HR management with leadership responsibility.",
      "Deep expertise in strategic workforce planning, talent acquisition, and labor laws.",
      "Strong executive presence and ability to coach C-level executives on people strategy."
    ],
    "preferred": [
      "Experience driving HR tech transformations or scaling teams from 100 to 500+."
    ],
    "benefits": [
      "Executive health cover",
      "ESOP stock grants",
      "Annual performance incentives",
      "Leadership retreats"
    ],
    "faqs": [
      {
        "q": "What is the reporting line?",
        "a": "This role reports directly to the Chief Executive Officer (CEO)."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Admin Executive",
    "slug": "admin-executive-job-description",
    "department": "Human Resources & Admin",
    "level": "Entry-Mid",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹2,50,000 - ₹4,50,000 P.A.",
    "summary": "Ensure smooth day-to-day office operations, manage facility vendors, supervise pantry and housekeeping staff, and coordinate office supplies.",
    "responsibilities": [
      "Manage front desk, guest reception, courier handling, and visitor records.",
      "Supervise facility maintenance, housekeeping, air conditioning, and electrical systems.",
      "Maintain inventory of stationery, pantry supplies, printing consumables, and first-aid kits.",
      "Coordinate domestic travel bookings (flights, trains, hotels, cabs) for executives."
    ],
    "requirements": [
      "Bachelor's degree in any stream.",
      "1+ years experience in office administration or facility coordination.",
      "Good organizational skills, proactive attitude, and phone etiquette.",
      "Proficiency in MS Excel and Word."
    ],
    "preferred": [
      "Experience managing modern co-working or corporate offices."
    ],
    "benefits": [
      "Health insurance",
      "Provident fund",
      "Annual performance increment"
    ],
    "faqs": [
      {
        "q": "What are the working hours?",
        "a": "General day shift, 9:00 AM to 6:00 PM."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Administration Manager",
    "slug": "administration-manager-job-description",
    "department": "Human Resources & Admin",
    "level": "Manager",
    "experience": "6 - 10 Years",
    "type": "Full-Time",
    "salary": "₹8,00,000 - ₹15,00,000 P.A.",
    "summary": "Oversee complete workplace facilities, commercial real estate leases, security operations, vendor contracts, transport logistics, and workplace safety protocols.",
    "responsibilities": [
      "Oversee multi-floor office infrastructure, lease renewals, and interior fit-out projects.",
      "Negotiate and manage annual maintenance contracts (AMCs) for HVAC, DG sets, and cafeteria.",
      "Enforce environmental health & safety (EHS) protocols, fire safety drills, and emergency readiness.",
      "Manage corporate administration budget, expense audits, and cost optimization initiatives."
    ],
    "requirements": [
      "Bachelor's / Master's degree in Business Administration or Facilities Management.",
      "6+ years experience in corporate administration with team management.",
      "Expertise in lease negotiations, vendor management, and legal compliance.",
      "Demonstrated crisis management and emergency response capabilities."
    ],
    "preferred": [
      "Certified Facility Manager (CFM) credential."
    ],
    "benefits": [
      "Health insurance for family",
      "Performance bonus",
      "Car allowance",
      "Paid vacation leave"
    ],
    "faqs": [
      {
        "q": "How many square feet of facility?",
        "a": "Approximately 35,000 sq. ft. across two corporate offices."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Office Administrator",
    "slug": "office-administrator-job-description",
    "department": "Human Resources & Admin",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time",
    "salary": "₹3,50,000 - ₹6,00,000 P.A.",
    "summary": "Coordinate office procedures, liaise with building management, oversee administrative budgets, and provide logistical support for company events.",
    "responsibilities": [
      "Supervise daily administrative staff, security guards, and front-desk coordinators.",
      "Monitor monthly utility bills, vendor invoices, and petty cash expenditure.",
      "Plan logistics for annual conferences, team offsites, and board meetings.",
      "Ensure compliance with local municipal shop & establishment regulations."
    ],
    "requirements": [
      "Graduate with 2+ years of administrative leadership experience.",
      "Strong communication, multitasking, and problem-solving abilities.",
      "Proficiency with office management software and spreadsheets."
    ],
    "preferred": [
      "Experience in IT/ITeS corporate environments."
    ],
    "benefits": [
      "Medical insurance",
      "PF & Gratuity",
      "Annual paid leave"
    ],
    "faqs": [
      {
        "q": "Will I manage staff?",
        "a": "Yes, you will oversee 4-6 facility and support staff."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Executive Assistant",
    "slug": "executive-assistant-job-description",
    "department": "Human Resources & Admin",
    "level": "Mid-Senior",
    "experience": "3 - 7 Years",
    "type": "Full-Time",
    "salary": "₹6,00,000 - ₹13,00,000 P.A.",
    "summary": "Provide high-level confidential executive support to the CEO and Board of Directors, managing complex calendars, board decks, travel itineraries, and key communications.",
    "responsibilities": [
      "Manage executive calendar, meeting requests, and gatekeep access to leadership time.",
      "Prepare board meeting agendas, record minutes, and track action items to closure.",
      "Draft executive correspondence, briefing documents, speeches, and presentations.",
      "Coordinate intricate international travel itineraries, visas, and accommodations.",
      "Handle sensitive and confidential corporate matters with utmost discretion."
    ],
    "requirements": [
      "Bachelor's or Master's degree in English, Business, or Communications.",
      "3+ years experience supporting C-Suite executives (CEO, MD, Chairperson).",
      "Flawless written and spoken English with exceptional presentation skills.",
      "Mastery of Google Workspace, MS PowerPoint, and calendar management."
    ],
    "preferred": [
      "Prior experience in multinational companies or venture-backed startups."
    ],
    "benefits": [
      "Premium medical coverage",
      "Annual performance bonus",
      "Executive travel perks",
      "Work flexibility"
    ],
    "faqs": [
      {
        "q": "What is the expected availability?",
        "a": "Standard business hours with reasonable flexibility during board weeks."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Marketing Executive",
    "slug": "marketing-executive-job-description",
    "department": "Marketing & Creative",
    "level": "Entry-Mid",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹3,50,000 - ₹6,00,000 P.A.",
    "summary": "Support creative marketing campaigns, manage social media handles, coordinate webinars, and assist in generating qualified leads through inbound channels.",
    "responsibilities": [
      "Plan and schedule engaging social media posts across LinkedIn, Twitter, and Instagram.",
      "Coordinate webinars, email newsletters, and product launch announcements.",
      "Collaborate with graphic designers and content writers to produce campaign collateral.",
      "Monitor marketing campaign analytics (impressions, clicks, conversions) in Google Analytics."
    ],
    "requirements": [
      "Bachelor's degree in Marketing, Mass Media, or Communications.",
      "1+ years experience in digital marketing or brand communications.",
      "Familiarity with social media management tools (Buffer, Hootsuite) and Canva.",
      "Creative writing flair and keen eye for design aesthetics."
    ],
    "preferred": [
      "Basic understanding of SEO and email marketing automation tools."
    ],
    "benefits": [
      "Medical insurance",
      "Learning stipend",
      "Quarterly team outings"
    ],
    "faqs": [
      {
        "q": "What is the focus: B2B or B2C?",
        "a": "This is 100% B2B software marketing targeting HR and business leaders."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Marketing Manager",
    "slug": "marketing-manager-job-description",
    "department": "Marketing & Creative",
    "level": "Manager",
    "experience": "5 - 9 Years",
    "type": "Full-Time",
    "salary": "₹11,00,000 - ₹22,00,000 P.A.",
    "summary": "Lead brand marketing strategy, customer acquisition funnels, product marketing launches, and manage an integrated team of writers, designers, and growth marketers.",
    "responsibilities": [
      "Develop comprehensive go-to-market (GTM) strategies for software product lines.",
      "Allocate marketing budgets across organic SEO, paid ads (Google/LinkedIn), and field events.",
      "Drive lead generation funnels and collaborate with sales to increase lead-to-opportunity velocity.",
      "Track and report on CAC, payback periods, pipeline contribution, and brand sentiment."
    ],
    "requirements": [
      "MBA in Marketing or equivalent degree.",
      "5+ years of B2B marketing experience with proven team leadership.",
      "Deep understanding of digital marketing funnels, performance marketing, and branding.",
      "Data-driven decision making with Google Analytics, Semrush, and CRM attribution."
    ],
    "preferred": [
      "Experience in SaaS or high-growth tech scaleups."
    ],
    "benefits": [
      "Executive health plan",
      "Performance bonus",
      "ESOP equity",
      "Flexible hybrid schedule"
    ],
    "faqs": [
      {
        "q": "What is the annual marketing budget?",
        "a": "You will manage a multi-crore annual acquisition and branding budget."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Digital Marketing Manager",
    "slug": "digital-marketing-manager-job-description",
    "department": "Marketing & Creative",
    "level": "Mid-Senior",
    "experience": "4 - 8 Years",
    "type": "Full-Time",
    "salary": "₹8,00,000 - ₹16,00,000 P.A.",
    "summary": "Drive performance marketing campaigns across Google Ads, LinkedIn Ads, Meta, and lead SEO initiatives to scale organic website traffic and demo signups.",
    "responsibilities": [
      "Manage high-ROI paid search, display, and paid social campaigns with strict CAC targets.",
      "Lead organic SEO strategy: keyword research, content briefs, backlink acquisition, and on-page optimization.",
      "A/B test landing page copy, forms, and creative assets to maximize conversion rates.",
      "Analyze attribution models and marketing funnel conversion in GA4 and HubSpot."
    ],
    "requirements": [
      "4+ years hands-on experience in B2B performance marketing and SEO.",
      "Demonstrated success managing ₹5L+ monthly ad spends on Google & LinkedIn Ads.",
      "Technical SEO knowledge (site architecture, Core Web Vitals, schema markup).",
      "Google Ads and Google Analytics certified."
    ],
    "preferred": [
      "Experience with HubSpot Marketing Hub or Marketo."
    ],
    "benefits": [
      "Health coverage",
      "Quarterly performance bonus",
      "MacBook workstation",
      "Hybrid work"
    ],
    "faqs": [
      {
        "q": "What is the primary target?",
        "a": "Generating high-intent enterprise demo requests and free trial signups."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Content Writer",
    "slug": "content-writer-job-description",
    "department": "Marketing & Creative",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time / Remote",
    "salary": "₹4,50,000 - ₹8,50,000 P.A.",
    "summary": "Create authoritative, SEO-optimized articles, whitepapers, case studies, and HR policy guides that educate business leaders and rank #1 on Google search.",
    "responsibilities": [
      "Write high-ranking long-form blog articles (1,500 - 3,000 words) on HR, payroll, and labor laws.",
      "Interview product leaders and clients to craft compelling customer case studies.",
      "Write persuasive copy for landing pages, email nurture sequences, and downloadable templates.",
      "Conduct keyword research using Ahrefs/Semrush and optimize on-page content."
    ],
    "requirements": [
      "2+ years experience writing B2B content, preferably in HR tech, fintech, or SaaS.",
      "Impeccable English grammar, storytelling ability, and research rigor.",
      "Solid understanding of search engine optimization (SEO) best practices.",
      "Portfolio of published articles ranking on first-page Google results."
    ],
    "preferred": [
      "Understanding of Indian payroll and HR compliance topics."
    ],
    "benefits": [
      "Remote-first flexibility",
      "Health insurance",
      "Book & learning allowance",
      "Home office stipend"
    ],
    "faqs": [
      {
        "q": "Is this a remote role?",
        "a": "Yes, candidates anywhere in India with a strong portfolio can apply."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Graphic Designer",
    "slug": "graphic-designer-job-description",
    "department": "Marketing & Creative",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time",
    "salary": "₹4,00,000 - ₹8,00,000 P.A.",
    "summary": "Create visually stunning digital graphics, social media creatives, brand marketing collateral, UI illustrations, and presentation decks that elevate our brand identity.",
    "responsibilities": [
      "Design compelling social media banners, blog graphics, and infographic illustrations.",
      "Create high-converting ad creatives for Google Display and LinkedIn campaigns.",
      "Produce professional sales decks, product brochures, and booth graphics for expos.",
      "Maintain consistent typography, color palette, and visual brand guidelines."
    ],
    "requirements": [
      "Degree in Graphic Design, Fine Arts, or impressive design portfolio.",
      "Mastery of Adobe Creative Cloud (Photoshop, Illustrator, InDesign) and Figma.",
      "Strong understanding of composition, color theory, visual hierarchy, and branding.",
      "Ability to work under agile sprint deadlines and iterate on feedback."
    ],
    "preferred": [
      "Motion graphics / After Effects animation skills are a big plus."
    ],
    "benefits": [
      "High-spec Apple iMac/MacBook workstation",
      "Health insurance",
      "Creative freedom",
      "Team offsites"
    ],
    "faqs": [
      {
        "q": "Is a portfolio required?",
        "a": "Yes, applications without a Behance, Dribbble, or website link will not be reviewed."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Product Manager",
    "slug": "product-manager-job-description",
    "department": "Marketing & Creative",
    "level": "Mid-Senior",
    "experience": "3 - 7 Years",
    "type": "Full-Time",
    "salary": "₹12,00,000 - ₹25,00,000 P.A.",
    "summary": "Own product vision, roadmap strategy, feature prioritization, and user experience for our enterprise software modules, driving user adoption and retention.",
    "responsibilities": [
      "Conduct customer discovery interviews and analyze telemetry data to identify user pain points.",
      "Author detailed PRDs, user stories, and feature acceptance criteria in Jira.",
      "Work closely with engineering and UI/UX teams during sprint delivery cycles.",
      "Track product metrics: monthly active users (MAU), feature adoption, and task completion time.",
      "Lead cross-functional launch plans with sales, marketing, and customer support."
    ],
    "requirements": [
      "3+ years experience as a B2B SaaS Product Manager.",
      "Strong analytical ability with data-driven decision making (Mixpanel, PostHog, SQL).",
      "Demonstrated ability to translate complex business workflows into simple, elegant UX.",
      "Engineering or technical background is preferred."
    ],
    "preferred": [
      "Prior experience in HR tech, ERP, or fin-tech products."
    ],
    "benefits": [
      "Attractive ESOP stock options",
      "Comprehensive health coverage",
      "Flexible working environment",
      "Conference travel"
    ],
    "faqs": [
      {
        "q": "Which module will I own?",
        "a": "Depending on experience, you may own Payroll Automation, Core HRMS, or Billing."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Operations Executive",
    "slug": "operations-executive-job-description",
    "department": "Operations & Management",
    "level": "Entry-Mid",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹3,00,000 - ₹5,00,000 P.A.",
    "summary": "Coordinate daily business workflows, manage customer onboarding queues, resolve client operational requests, and maintain service SLAs.",
    "responsibilities": [
      "Manage client ticket queues, process system configuration requests, and resolve operational issues.",
      "Coordinate with technical support and billing teams to ensure smooth data migrations.",
      "Generate weekly operational reports, SLA compliance scores, and process bottleneck summaries.",
      "Identify workflow automation opportunities to reduce manual data entry."
    ],
    "requirements": [
      "Bachelor's degree in Business, Commerce, or related discipline.",
      "1+ years experience in B2B customer operations or service delivery.",
      "Strong problem-solving mindset and proficiency in Excel/Google Sheets.",
      "Clear written and verbal communication."
    ],
    "preferred": [
      "Familiarity with CRM ticketing tools (Zendesk, Freshdesk)."
    ],
    "benefits": [
      "Health insurance",
      "Quarterly incentives",
      "PF & Gratuity",
      "Career growth paths"
    ],
    "faqs": [
      {
        "q": "Is night shift required?",
        "a": "No, this is a standard day-shift role (9:30 AM to 6:30 PM)."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Operations Manager",
    "slug": "operations-manager-job-description",
    "department": "Operations & Management",
    "level": "Manager",
    "experience": "6 - 10 Years",
    "type": "Full-Time",
    "salary": "₹10,00,000 - ₹18,00,000 P.A.",
    "summary": "Oversee organizational operations, implement lean process improvements, optimize resource allocation, and ensure flawless client service delivery across teams.",
    "responsibilities": [
      "Lead a team of operations executives and project coordinators.",
      "Establish operational KPIs, turnaround time (TAT) targets, and quality benchmarks.",
      "Redesign broken operational processes using Six Sigma / Lean methodologies.",
      "Manage vendor contracts, logistics partners, and operational expense budgets."
    ],
    "requirements": [
      "6+ years experience in operations management with proven leadership track record.",
      "Strong analytical expertise in capacity planning, queue management, and metrics tracking.",
      "Excellent executive communication, stakeholder management, and conflict resolution skills.",
      "MBA from a reputable institution."
    ],
    "preferred": [
      "Six Sigma Green / Black Belt certification."
    ],
    "benefits": [
      "Executive health plan",
      "Performance bonus",
      "Car allowance",
      "Hybrid schedule"
    ],
    "faqs": [
      {
        "q": "What teams will I oversee?",
        "a": "Customer operations, implementation support, and vendor logistics."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Project Manager",
    "slug": "project-manager-job-description",
    "department": "Operations & Management",
    "level": "Mid-Senior",
    "experience": "4 - 8 Years",
    "type": "Full-Time",
    "salary": "₹8,00,000 - ₹16,00,000 P.A.",
    "summary": "Plan, execute, and deliver enterprise client implementations on schedule and within budget, managing project scope, milestones, and client stakeholder communications.",
    "responsibilities": [
      "Create detailed project implementation plans, milestone schedules, and resource allocations.",
      "Conduct weekly client steering committee meetings and report on progress.",
      "Manage project risks, scope changes, and technical blockers to ensure timely go-live.",
      "Oversee software customization testing and user acceptance sign-offs (UAT)."
    ],
    "requirements": [
      "4+ years experience in IT project management or software client implementations.",
      "Proficiency in Agile/Waterfall methodologies, Jira, MS Project, or Asana.",
      "Strong ability to manage difficult client situations and scope creep.",
      "PMP, PRINCE2, or CSM certification is a major plus."
    ],
    "preferred": [
      "Experience implementing ERP or HRMS software for 1,000+ employee clients."
    ],
    "benefits": [
      "Comprehensive medical cover",
      "Project completion bonus",
      "Learning allowance"
    ],
    "faqs": [
      {
        "q": "How many concurrent projects will I manage?",
        "a": "Typically 3-5 concurrent enterprise implementations."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Program Manager",
    "slug": "program-manager-job-description",
    "department": "Operations & Management",
    "level": "Senior",
    "experience": "7 - 12 Years",
    "type": "Full-Time",
    "salary": "₹14,00,000 - ₹28,00,000 P.A.",
    "summary": "Direct multiple interconnected enterprise initiatives, align cross-functional product and operational teams, and ensure delivery of strategic multi-year company goals.",
    "responsibilities": [
      "Define program governance, cross-project dependencies, and executive milestones.",
      "Align product engineering, sales, customer success, and legal teams on key initiatives.",
      "Present program health dashboards and strategic recommendations to executive leadership.",
      "Champion change management practices to ensure organizational adoption of new processes."
    ],
    "requirements": [
      "7+ years program management experience in high-growth technology environments.",
      "Proven track record delivering large-scale digital transformation initiatives.",
      "Outstanding strategic thinking, executive storytelling, and matrix management skills.",
      "PgMP or PMP credential preferred."
    ],
    "preferred": [
      "Experience managing enterprise SaaS migration programs."
    ],
    "benefits": [
      "Executive bonus scheme",
      "ESOP equity grants",
      "Premium health insurance",
      "Travel perks"
    ],
    "faqs": [
      {
        "q": "What is the reporting structure?",
        "a": "Reports directly to the Chief Operating Officer (COO)."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Branch Manager",
    "slug": "branch-manager-job-description",
    "department": "Operations & Management",
    "level": "Manager",
    "experience": "5 - 10 Years",
    "type": "Full-Time",
    "salary": "₹8,00,000 - ₹15,00,000 P.A.",
    "summary": "Manage overall regional branch performance, drive local sales revenue, oversee customer support operations, and maintain employee morale and regulatory compliance.",
    "responsibilities": [
      "Assume full P&L accountability for the regional office branch.",
      "Direct local sales, customer success, and field service personnel to exceed targets.",
      "Cultivate relationships with prominent local businesses, trade associations, and chamber of commerce.",
      "Ensure adherence to local state labor regulations, safety norms, and office administration standards."
    ],
    "requirements": [
      "5+ years of managerial experience in branch banking, retail management, or regional B2B operations.",
      "Strong commercial acumen, sales leadership, and people management skills.",
      "Fluency in local language and English.",
      "Bachelor's or Master's degree in Business."
    ],
    "preferred": [
      "Established local corporate network in the region."
    ],
    "benefits": [
      "Branch profit-share bonus",
      "Health insurance",
      "Car allowance",
      "Paid leave"
    ],
    "faqs": [
      {
        "q": "Which locations are hiring?",
        "a": "Branch openings across Pune, Bengaluru, Ahmedabad, and Delhi."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "General Manager",
    "slug": "general-manager-job-description",
    "department": "Operations & Management",
    "level": "Executive",
    "experience": "10 - 18 Years",
    "type": "Full-Time",
    "salary": "₹20,00,000 - ₹40,00,000 P.A.",
    "summary": "Oversee overall business unit operations, formulate growth strategies, manage comprehensive P&L budgets, and build high-performing leadership teams.",
    "responsibilities": [
      "Direct all business unit operations including sales, engineering delivery, HR, and finance.",
      "Formulate 3-year strategic business plans and annual operational budgets.",
      "Evaluate strategic partnership opportunities, expansion into international markets, and M&A.",
      "Report directly to the Board of Directors on business unit performance and quarterly metrics."
    ],
    "requirements": [
      "10+ years executive leadership experience managing entire business divisions.",
      "Proven track record of scaling annual revenues from ₹10Cr to ₹100Cr+.",
      "Exceptional visionary leadership, capital allocation, and team empowerment capability.",
      "Master's degree / MBA from a tier-1 institution."
    ],
    "preferred": [
      "Experience in enterprise software, industrial automation, or SaaS."
    ],
    "benefits": [
      "Significant equity / ESOP allocation",
      "Executive healthcare",
      "Performance profit share",
      "Car & driver allowance"
    ],
    "faqs": [
      {
        "q": "What is the scope of responsibility?",
        "a": "Full P&L responsibility for our Enterprise Products division."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Document Controller",
    "slug": "document-controller-job-description",
    "department": "Operations & Management",
    "level": "Entry-Mid",
    "experience": "1 - 4 Years",
    "type": "Full-Time",
    "salary": "₹2,50,000 - ₹4,50,000 P.A.",
    "summary": "Manage, archive, verify, and retrieve critical organizational contracts, legal agreements, compliance certificates, and client implementation files.",
    "responsibilities": [
      "Classify, index, and securely store corporate legal contracts, NDAs, and customer agreements.",
      "Enforce document version control, access privileges, and electronic signature protocols (DocuSign).",
      "Coordinate document retrieval for statutory audits, due diligence, and legal reviews.",
      "Digitize physical records and ensure compliance with ISO 27001 data retention standards."
    ],
    "requirements": [
      "Bachelor's degree in any discipline.",
      "1+ years experience in document management or corporate records keeping.",
      "High attention to detail and familiarity with document management software.",
      "Strong understanding of confidentiality and data security."
    ],
    "preferred": [
      "Experience in IT, legal, or construction document control."
    ],
    "benefits": [
      "Health coverage",
      "PF & Gratuity",
      "Annual increment"
    ],
    "faqs": [
      {
        "q": "Is electronic document management used?",
        "a": "Yes, we utilize cloud-based enterprise DMS with strict encryption."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "AR Caller",
    "slug": "ar-caller-job-description",
    "department": "Operations & Management",
    "level": "Entry-Level",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹2,80,000 - ₹5,00,000 P.A.",
    "summary": "Contact insurance payers and commercial clients to track claim status, resolve billing denials, and accelerate accounts receivable collections.",
    "responsibilities": [
      "Follow up on unpaid or denied claims with insurance providers and corporate clients via phone and web portals.",
      "Investigate rejection codes, correct billing discrepancies, and resubmit documentation.",
      "Maintain detailed call logs, denial trends, and payment resolution timelines in software.",
      "Achieve monthly collection quotas and reduce days sales outstanding (DSO)."
    ],
    "requirements": [
      "1+ years experience as an AR Caller in US healthcare RCM or commercial B2B collections.",
      "Excellent verbal English communication and active listening skills.",
      "Knowledge of denial codes, appeals, and revenue cycle management.",
      "Comfortable working in dedicated calling shifts."
    ],
    "preferred": [
      "Experience with major US payers or enterprise B2B collections."
    ],
    "benefits": [
      "Monthly collection incentives",
      "Cab pickup & drop facility",
      "Health insurance",
      "Shift allowances"
    ],
    "faqs": [
      {
        "q": "What shifts are available?",
        "a": "We have both general day shift and US night shift opportunities with shift allowance."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Assistant Manager",
    "slug": "assistant-manager-job-description",
    "department": "Operations & Management",
    "level": "Mid-Level",
    "experience": "3 - 6 Years",
    "type": "Full-Time",
    "salary": "₹5,50,000 - ₹10,00,000 P.A.",
    "summary": "Support the Department Head in coordinating daily team workflows, monitoring performance metrics, mentoring junior staff, and resolving escalations.",
    "responsibilities": [
      "Assist the Manager in delegating daily tasks and monitoring deliverable deadlines.",
      "Conduct regular quality audits and coach junior team members on process improvements.",
      "Step in as acting manager during the manager's absence to maintain business continuity.",
      "Prepare operational progress reports and KPI updates for leadership."
    ],
    "requirements": [
      "Bachelor's / Master's degree in Management or related discipline.",
      "3+ years experience with demonstrated leadership potential.",
      "Strong analytical ability, problem-solving, and communication skills.",
      "Proficiency in project tracking tools and reporting."
    ],
    "preferred": [
      "Experience in IT services, customer operations, or logistics."
    ],
    "benefits": [
      "Medical insurance",
      "Annual bonus",
      "Fast-track management progression",
      "Paid leaves"
    ],
    "faqs": [
      {
        "q": "What is the path to Manager?",
        "a": "High-performing Assistant Managers typically advance to full Manager within 18-24 months."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Customer Support Representative",
    "slug": "customer-support-representative-job-description",
    "department": "Operations & Management",
    "level": "Entry-Mid",
    "experience": "1 - 3 Years",
    "type": "Full-Time",
    "salary": "₹2,80,000 - ₹5,00,000 P.A.",
    "summary": "Deliver friendly, efficient, and empathetic technical and billing support to HR software users via live chat, email ticketing, and phone.",
    "responsibilities": [
      "Respond to incoming user queries with first-contact resolution focus.",
      "Troubleshoot software configuration, biometric sync, and payroll calculation questions.",
      "Document bug reports and feature requests for engineering teams in Jira.",
      "Author helpful knowledge base guides and tutorial articles for customers."
    ],
    "requirements": [
      "1+ years experience in customer support for tech or software products.",
      "Excellent written and verbal communication skills in English and Hindi.",
      "Patience, empathy, and positive customer-centric attitude.",
      "Familiarity with helpdesk platforms (Zendesk, Freshdesk, Intercom)."
    ],
    "preferred": [
      "Understanding of payroll or attendance systems."
    ],
    "benefits": [
      "Health insurance",
      "Performance incentives",
      "Rotational comp-offs",
      "Employee wellness programs"
    ],
    "faqs": [
      {
        "q": "What support channels are handled?",
        "a": "Primarily web chat and email tickets with occasional scheduled phone assistance."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "QA Engineer",
    "slug": "qa-engineer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time",
    "salary": "₹5,00,000 - ₹11,00,000 P.A.",
    "summary": "Ensure exceptional software quality by designing automated and manual test suites, identifying edge-case bugs, and executing regression runs.",
    "responsibilities": [
      "Write comprehensive test plans, test cases, and acceptance scenarios for new features.",
      "Build and maintain automated test scripts using Selenium, Playwright, or Cypress.",
      "Execute API functional testing using Postman, Newman, and RestAssured.",
      "Track, document, and verify bug fixes in Jira with clear reproduction steps."
    ],
    "requirements": [
      "2+ years experience in software quality assurance (manual + automation).",
      "Hands-on scripting ability in JavaScript, TypeScript, or Python.",
      "Experience with web, mobile, and API testing methodologies.",
      "Familiarity with CI/CD integration and agile sprint cycles."
    ],
    "preferred": [
      "ISTQB certification.",
      "Performance testing experience with JMeter or k6."
    ],
    "benefits": [
      "Health insurance",
      "Annual bonus",
      "MacBook workstation",
      "Hybrid work flexibility"
    ],
    "faqs": [
      {
        "q": "What is the automation vs manual ratio?",
        "a": "We strive for 70% automated test coverage across our core regression suites."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Technical Recruiter",
    "slug": "technical-recruiter-job-description",
    "department": "Human Resources & Admin",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time",
    "salary": "₹4,50,000 - ₹9,00,000 + Hiring Incentives",
    "summary": "Source, attract, and hire exceptional software engineering, design, and product talent across India's premier tech hubs.",
    "responsibilities": [
      "Partner with engineering hiring managers to define technical requirements and ideal candidate personas.",
      "Source passive engineering talent via LinkedIn Recruiter, GitHub, and tech communities.",
      "Conduct technical phone screens and manage the interview process through our ATS.",
      "Negotiate competitive salary packages, close offers, and ensure exceptional candidate experience."
    ],
    "requirements": [
      "2+ years experience recruiting tech talent (developers, DevOps, product managers).",
      "Strong understanding of modern tech stacks (React, Node, Python, AWS, microservices).",
      "Proven ability to source niche technical candidates and build active talent pipelines.",
      "Familiarity with applicant tracking systems and recruiting metrics (time-to-hire, offer acceptance)."
    ],
    "preferred": [
      "Agency or fast-paced product startup recruiting experience."
    ],
    "benefits": [
      "Lucrative per-hire placement bonuses",
      "Medical coverage",
      "Hybrid flexibility",
      "Learning budget"
    ],
    "faqs": [
      {
        "q": "What is the monthly hiring target?",
        "a": "Typically 3-5 technical engineering hires per month."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "Warehouse / Logistics Manager",
    "slug": "warehouse-manager-job-description",
    "department": "Operations & Management",
    "level": "Manager",
    "experience": "5 - 9 Years",
    "type": "Full-Time",
    "salary": "₹6,00,000 - ₹12,00,000 P.A.",
    "summary": "Manage daily warehouse operations, inventory tracking, dispatch logistics, hardware terminal shipping, and maintain safety and audit compliance.",
    "responsibilities": [
      "Oversee storage, picking, packaging, and dispatch of biometric hardware and office equipment.",
      "Implement barcode scanning, cycle counting, and perpetual inventory reconciliations in ERP.",
      "Negotiate shipping rates with courier partners (Bluedart, Delhivery) and monitor delivery TAT.",
      "Enforce warehouse safety standards, fire regulations, and team productivity goals."
    ],
    "requirements": [
      "5+ years experience in warehouse or logistics management.",
      "Strong knowledge of inventory management systems and supply chain logistics.",
      "Proven team leadership over warehouse staff and forklift operators.",
      "Bachelor's degree in Supply Chain, Commerce, or related discipline."
    ],
    "preferred": [
      "Experience managing electronic or hardware device logistics."
    ],
    "benefits": [
      "Health coverage",
      "Annual performance bonus",
      "PF & Gratuity",
      "Paid leaves"
    ],
    "faqs": [
      {
        "q": "What is the inventory volume?",
        "a": "Managing storage and dispatch of 10,000+ biometric units annually."
      }
    ],
    "templateText": "# ${r.title}\n\n## Role Overview\n${r.summary}\n\n- **Department:** ${r.department}\n- **Experience Level:** ${r.experience} (${r.level})\n- **Employment Type:** ${r.type}\n- **Salary Range:** ${r.salary}\n- **Location:** [Insert City / Remote / Hybrid]\n- **Reports To:** [Insert Hiring Manager Title]\n\n---\n\n## Key Responsibilities\n${r.responsibilities.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Qualifications & Skills\n${r.requirements.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Preferred Qualifications\n${r.preferred.map(item => '- ' + item).join('\\n')}\n\n---\n\n## Benefits & Perks\n${r.benefits.map(item => '- ' + item).join('\\n')}\n- Statutory PF, ESI & Gratuity compliance\n- Equal Opportunity Employer commitment\n\n---\n\n### How to Apply\nInterested candidates are invited to submit their resume and portfolio to [careers@company.com] with the subject line \"Application for ${r.title}\".\n"
  },
  {
    "title": "UI/UX Designer",
    "slug": "ui-ux-designer-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹7,00,000 - ₹15,00,000 P.A.",
    "summary": "We are looking for a UI/UX Designer to craft intuitive, accessible product experiences across web and mobile. You will own the design process from research through high-fidelity prototypes in close partnership with product and engineering.",
    "responsibilities": [
      "Conduct user research, usability testing, and translate findings into actionable design decisions.",
      "Create wireframes, interactive prototypes, and high-fidelity UI designs in Figma.",
      "Maintain and evolve the company's design system and component library.",
      "Collaborate closely with product managers and engineers to ensure design feasibility and fidelity.",
      "Run design critiques and incorporate stakeholder feedback into iterative design cycles.",
      "Champion accessibility (WCAG) standards across all shipped product surfaces."
    ],
    "requirements": [
      "Bachelor's degree in Design, HCI, or a related field, or equivalent practical experience.",
      "2+ years of product design experience with a strong portfolio demonstrating end-to-end process.",
      "Proficiency in Figma and prototyping tools; working knowledge of design tokens and systems.",
      "Solid understanding of responsive design, accessibility, and cross-platform UI patterns.",
      "Ability to communicate design rationale clearly to both technical and non-technical stakeholders."
    ],
    "preferred": [
      "Experience designing for enterprise SaaS or HR/fintech products.",
      "Basic familiarity with HTML/CSS to collaborate effectively with frontend engineers.",
      "Experience running moderated user research sessions."
    ],
    "benefits": [
      "Competitive compensation with annual performance appraisal and bonus incentives.",
      "Comprehensive group health insurance covering employee and immediate family.",
      "Flexible hybrid work policy with home office ergonomics allowance.",
      "Annual learning and development stipend for certifications, conferences, and courses."
    ],
    "faqs": [
      {
        "q": "What design tools does the team use?",
        "a": "Our core workflow is Figma for design and prototyping, with a shared component library maintained by the design systems lead."
      },
      {
        "q": "Is a portfolio required?",
        "a": "Yes, we ask all candidates to walk us through 2-3 case studies showing your process from research to final UI."
      }
    ]
  },
  {
    "title": "Payroll Executive",
    "slug": "payroll-executive-job-description",
    "department": "Finance & Accounting",
    "level": "Junior to Mid-Level",
    "experience": "1 - 4 Years",
    "type": "Full-Time / On-site",
    "salary": "₹3,50,000 - ₹6,50,000 P.A.",
    "summary": "We are hiring a Payroll Executive to process monthly payroll accurately and on time, ensuring full statutory compliance across PF, ESI, Professional Tax, and TDS for our growing workforce.",
    "responsibilities": [
      "Process monthly payroll inputs including attendance, leave, and reimbursement claims.",
      "Calculate and validate PF, ESI, Professional Tax, and TDS deductions for each pay cycle.",
      "Generate and distribute digital payslips and Form 130 (Form 16) documents to employees.",
      "Prepare ECR files and coordinate statutory filings with the compliance team.",
      "Resolve employee payroll queries within agreed turnaround time.",
      "Reconcile payroll registers against the general ledger every month-end."
    ],
    "requirements": [
      "Bachelor's degree in Commerce, Finance, or a related field.",
      "1+ years of hands-on Indian payroll processing experience.",
      "Working knowledge of PF, ESI, Professional Tax, and TDS calculation rules.",
      "Proficiency in MS Excel and experience with any payroll or HRMS software.",
      "High attention to detail and ability to meet strict monthly payroll deadlines."
    ],
    "preferred": [
      "Experience with multi-state payroll processing.",
      "Exposure to statutory audits and labour inspections."
    ],
    "benefits": [
      "Performance-linked annual bonus and yearly appraisal cycle.",
      "Group health insurance for employee and family.",
      "Structured training on statutory compliance updates.",
      "Hybrid work flexibility during non-payroll-close weeks."
    ],
    "faqs": [
      {
        "q": "How many employees will this role support?",
        "a": "This role currently supports payroll for a workforce of 300+ employees across multiple states, growing steadily."
      },
      {
        "q": "Is prior HRMS software experience required?",
        "a": "Not mandatory, but experience with any cloud payroll or HRMS platform is a strong advantage."
      }
    ]
  },
  {
    "title": "Talent Acquisition Specialist",
    "slug": "talent-acquisition-specialist-job-description",
    "department": "Human Resources & Admin",
    "level": "Mid-Level",
    "experience": "2 - 6 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹5,00,000 - ₹10,00,000 P.A.",
    "summary": "We are seeking a Talent Acquisition Specialist to own end-to-end hiring for assigned business units — from sourcing and screening through offer negotiation and onboarding handoff.",
    "responsibilities": [
      "Partner with hiring managers to define role requirements and hiring plans.",
      "Source candidates through job boards, LinkedIn, referrals, and campus channels.",
      "Screen resumes and conduct initial phone or video interviews.",
      "Coordinate interview panels, schedules, and candidate communication throughout the process.",
      "Negotiate offers and manage the candidate experience through to joining.",
      "Track hiring funnel metrics and report on time-to-fill and source effectiveness."
    ],
    "requirements": [
      "Bachelor's degree in HR, Business Administration, or related field.",
      "2+ years of full-cycle recruitment experience, ideally in a fast-growing company.",
      "Strong sourcing skills across LinkedIn Recruiter, job boards, and referral networks.",
      "Excellent stakeholder management and candidate communication skills.",
      "Familiarity with applicant tracking systems (ATS) and structured interviewing."
    ],
    "preferred": [
      "Experience hiring for both technical and non-technical roles.",
      "Prior experience in a startup or high-growth environment."
    ],
    "benefits": [
      "Competitive compensation with recruitment incentive bonuses.",
      "Comprehensive group health insurance.",
      "Hybrid work policy with flexible hours.",
      "Access to premium recruiting tools and databases."
    ],
    "faqs": [
      {
        "q": "How many open roles will I manage at once?",
        "a": "Typically 8-12 active requisitions across a mix of technical and business roles at any given time."
      },
      {
        "q": "Does this role involve campus hiring?",
        "a": "Yes, campus hiring drives are part of the annual hiring calendar alongside lateral recruitment."
      }
    ]
  },
  {
    "title": "Social Media Manager",
    "slug": "social-media-manager-job-description",
    "department": "Marketing & Creative",
    "level": "Mid-Level",
    "experience": "2 - 5 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹5,00,000 - ₹9,00,000 P.A.",
    "summary": "We are looking for a Social Media Manager to grow our brand presence across platforms, plan and execute content calendars, and turn engagement into measurable pipeline for the business.",
    "responsibilities": [
      "Plan and execute monthly content calendars across LinkedIn, Instagram, X, and YouTube.",
      "Collaborate with design and content teams to produce on-brand creative assets.",
      "Monitor engagement, respond to comments and messages, and manage community sentiment.",
      "Track and report on key social metrics — reach, engagement rate, and lead attribution.",
      "Run and optimize paid social campaigns in coordination with the performance marketing team.",
      "Stay current on platform trends and adapt content strategy accordingly."
    ],
    "requirements": [
      "Bachelor's degree in Marketing, Communications, or a related field.",
      "2+ years of hands-on social media management experience for a brand or agency.",
      "Strong copywriting skills and an eye for visual content quality.",
      "Experience with social scheduling and analytics tools.",
      "Comfort working with performance data to guide content decisions."
    ],
    "preferred": [
      "Experience running paid social campaigns on Meta and LinkedIn Ads.",
      "Basic video editing or Canva/Figma design skills."
    ],
    "benefits": [
      "Performance bonuses tied to campaign and engagement targets.",
      "Group health insurance for employee and family.",
      "Hybrid work model with flexible hours.",
      "Annual budget for courses, certifications, and creative tools."
    ],
    "faqs": [
      {
        "q": "Which platforms will I primarily own?",
        "a": "LinkedIn and Instagram are our primary channels today, with X and YouTube as secondary and growing priorities."
      },
      {
        "q": "Will I manage paid campaigns too, or only organic?",
        "a": "Both — you'll own organic content strategy and work alongside performance marketing on paid social."
      }
    ]
  },
  {
    "title": "Customer Success Manager",
    "slug": "customer-success-manager-job-description",
    "department": "Sales & Business Development",
    "level": "Mid-Level",
    "experience": "3 - 6 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹8,00,000 - ₹16,00,000 P.A.",
    "summary": "We are hiring a Customer Success Manager to own post-sale relationships for a portfolio of accounts — driving adoption, renewal, and expansion while acting as the customer's advocate internally.",
    "responsibilities": [
      "Own onboarding, adoption, and ongoing success planning for an assigned book of accounts.",
      "Conduct regular business reviews to demonstrate ROI and identify expansion opportunities.",
      "Monitor product usage and health scores to proactively flag churn risk.",
      "Coordinate with support and product teams to resolve escalations quickly.",
      "Drive renewal conversations and negotiate contract terms ahead of expiry.",
      "Gather customer feedback and channel it into the product roadmap process."
    ],
    "requirements": [
      "Bachelor's degree in Business, Communications, or a related field.",
      "3+ years in a customer success, account management, or client-facing SaaS role.",
      "Strong communication and relationship-building skills with senior stakeholders.",
      "Comfort working with CRM and customer health/analytics tools.",
      "A consultative approach to identifying customer needs and mapping them to product value."
    ],
    "preferred": [
      "Experience in HR tech, fintech, or B2B SaaS accounts.",
      "Familiarity with renewal and expansion quota-carrying roles."
    ],
    "benefits": [
      "Base salary plus renewal and expansion incentive bonuses.",
      "Comprehensive group health insurance for employee and family.",
      "Hybrid work flexibility with quarterly in-person account reviews.",
      "Clear career path into senior CSM or account management leadership."
    ],
    "faqs": [
      {
        "q": "How large is a typical account portfolio?",
        "a": "CSMs typically manage 25-40 mid-market accounts, segmented by ARR tier and complexity."
      },
      {
        "q": "Is this role quota-carrying?",
        "a": "Yes, renewal rate and net revenue retention targets are part of the role's variable compensation."
      }
    ]
  },
  {
    "title": "Supply Chain Manager",
    "slug": "supply-chain-manager-job-description",
    "department": "Operations & Management",
    "level": "Senior",
    "experience": "5 - 10 Years",
    "type": "Full-Time / On-site",
    "salary": "₹10,00,000 - ₹20,00,000 P.A.",
    "summary": "We are looking for a Supply Chain Manager to oversee procurement, inventory planning, and logistics across our distribution network, ensuring cost efficiency and on-time delivery.",
    "responsibilities": [
      "Manage end-to-end supply chain operations including procurement, warehousing, and logistics.",
      "Forecast demand and plan inventory levels to balance cost with service levels.",
      "Negotiate contracts and manage relationships with key vendors and logistics partners.",
      "Identify and implement process improvements to reduce lead time and total supply chain cost.",
      "Monitor KPIs such as fill rate, inventory turnover, and on-time delivery performance.",
      "Lead and develop a team of procurement and logistics coordinators."
    ],
    "requirements": [
      "Bachelor's degree in Supply Chain, Operations, or Engineering; MBA preferred.",
      "5+ years of progressive experience in supply chain or logistics management.",
      "Strong analytical skills with experience using ERP and inventory planning tools.",
      "Proven vendor negotiation and contract management experience.",
      "Demonstrated ability to lead cross-functional teams and drive process change."
    ],
    "preferred": [
      "Experience in manufacturing, retail, or e-commerce supply chains.",
      "Certification in Six Sigma or supply chain management (CSCP/CPIM)."
    ],
    "benefits": [
      "Competitive compensation with annual performance bonus.",
      "Comprehensive group health and accident insurance.",
      "Company vehicle or travel allowance for site visits.",
      "Leadership development and cross-functional exposure programs."
    ],
    "faqs": [
      {
        "q": "How many warehouse/distribution locations does this role oversee?",
        "a": "This role currently oversees coordination across 6 regional distribution hubs, with plans to expand."
      },
      {
        "q": "Is travel required?",
        "a": "Yes, periodic travel to distribution sites and vendor locations is expected, roughly once or twice a month."
      }
    ]
  },
  {
    "title": "Legal Counsel",
    "slug": "legal-counsel-job-description",
    "department": "Operations & Management",
    "level": "Senior",
    "experience": "5 - 9 Years",
    "type": "Full-Time / On-site",
    "salary": "₹15,00,000 - ₹28,00,000 P.A.",
    "summary": "We are seeking a Legal Counsel to manage contracts, corporate compliance, and legal risk across the business, working closely with leadership on commercial and employment matters.",
    "responsibilities": [
      "Draft, review, and negotiate commercial contracts including vendor, customer, and partnership agreements.",
      "Advise HR and leadership on employment law, labour compliance, and policy matters.",
      "Manage corporate governance filings and liaise with external counsel where needed.",
      "Identify and mitigate legal risk across new business initiatives and product launches.",
      "Handle dispute resolution, notices, and pre-litigation matters.",
      "Maintain and update the company's contract templates and legal playbooks."
    ],
    "requirements": [
      "LLB from a recognized university; LLM preferred.",
      "5+ years post-qualification experience, ideally in-house or at a corporate law firm.",
      "Strong drafting and negotiation skills across commercial and employment contracts.",
      "Working knowledge of Indian labour codes, data protection law, and corporate compliance.",
      "Ability to communicate legal risk clearly to non-legal stakeholders."
    ],
    "preferred": [
      "Experience advising a SaaS or technology company.",
      "Exposure to data privacy compliance (DPDP Act) and IP matters."
    ],
    "benefits": [
      "Competitive compensation with annual performance bonus.",
      "Comprehensive group health and life insurance.",
      "Professional membership and continuing legal education support.",
      "Direct access and visibility with the leadership team."
    ],
    "faqs": [
      {
        "q": "Will this role manage external law firm relationships?",
        "a": "Yes, for specialized matters like litigation or IP filings, this role will brief and manage external counsel."
      },
      {
        "q": "Does this role report to the CEO or a legal head?",
        "a": "This role reports directly to the CEO, with a dotted line to the Head of Finance on compliance matters."
      }
    ]
  },
  {
    "title": "Compliance Officer",
    "slug": "compliance-officer-job-description",
    "department": "Operations & Management",
    "level": "Mid to Senior",
    "experience": "4 - 8 Years",
    "type": "Full-Time / On-site",
    "salary": "₹9,00,000 - ₹16,00,000 P.A.",
    "summary": "We are hiring a Compliance Officer to oversee statutory and regulatory compliance across HR, payroll, and business operations, ensuring the organization stays ahead of evolving labour and tax law.",
    "responsibilities": [
      "Track statutory compliance obligations across PF, ESI, Professional Tax, and labour welfare fund filings.",
      "Conduct periodic internal audits to identify and remediate compliance gaps.",
      "Liaise with statutory auditors, labour inspectors, and government authorities as needed.",
      "Maintain an up-to-date compliance calendar and filing tracker across all business locations.",
      "Advise HR and finance teams on new labour code changes and their operational impact.",
      "Prepare compliance reports and dashboards for leadership review."
    ],
    "requirements": [
      "Bachelor's degree in Law, Commerce, or HR; professional certification in labour law is a plus.",
      "4+ years of experience in statutory compliance, ideally across multiple Indian states.",
      "Strong working knowledge of PF, ESI, Professional Tax, and Shops & Establishments Act requirements.",
      "High attention to detail with strong documentation and audit-readiness practices.",
      "Comfort working directly with government authorities and external auditors."
    ],
    "preferred": [
      "Experience in a multi-state, multi-entity organization.",
      "Exposure to HRMS-driven compliance automation tools."
    ],
    "benefits": [
      "Competitive compensation with annual performance bonus.",
      "Comprehensive group health insurance for employee and family.",
      "Ongoing training on regulatory and labour law updates.",
      "Clear visibility into leadership decision-making on compliance matters."
    ],
    "faqs": [
      {
        "q": "How many states does this role cover?",
        "a": "This role currently covers compliance across 12 states, with responsibility growing as we expand into new locations."
      },
      {
        "q": "Does this role handle audits directly?",
        "a": "Yes, this role is the primary point of contact for statutory and labour audits, working alongside external auditors."
      }
    ]
  },
  {
    "title": "Data Scientist",
    "slug": "data-scientist-job-description",
    "department": "Engineering & IT",
    "level": "Mid-Level",
    "experience": "3 - 6 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹12,00,000 - ₹22,00,000 P.A.",
    "summary": "We are looking for a Data Scientist to build predictive models and analytics products that power workforce and business insights across our platform.",
    "responsibilities": [
      "Design and build predictive models for attrition risk, payroll anomaly detection, and workforce analytics.",
      "Partner with product and engineering to ship data-driven features into the core product.",
      "Clean, transform, and validate large datasets from multiple internal and external sources.",
      "Communicate findings and model performance clearly to non-technical stakeholders.",
      "Monitor deployed models for drift and retrain as needed.",
      "Contribute to the team's data pipeline and feature store infrastructure."
    ],
    "requirements": [
      "Bachelor's or Master's degree in Computer Science, Statistics, or a related quantitative field.",
      "3+ years of experience building and deploying machine learning models in production.",
      "Strong proficiency in Python, SQL, and common ML frameworks (scikit-learn, PyTorch, or TensorFlow).",
      "Solid understanding of statistics, experimentation design, and model evaluation.",
      "Ability to translate ambiguous business problems into well-scoped modeling projects."
    ],
    "preferred": [
      "Experience with HR, payroll, or workforce analytics data.",
      "Familiarity with cloud ML platforms (AWS SageMaker, GCP Vertex AI)."
    ],
    "benefits": [
      "Competitive compensation with annual performance appraisal and bonus.",
      "Comprehensive group health insurance covering employee and immediate family.",
      "Flexible hybrid work policy with home office allowance.",
      "Dedicated budget for conferences, courses, and compute resources."
    ],
    "faqs": [
      {
        "q": "What kind of data will I be working with?",
        "a": "Primarily anonymized attendance, payroll, and engagement data across our customer base, used to power in-product insights."
      },
      {
        "q": "Is this a research role or product-focused?",
        "a": "Product-focused — models you build ship directly into customer-facing features, not purely research output."
      }
    ]
  },
  {
    "title": "Site Reliability Engineer",
    "slug": "site-reliability-engineer-job-description",
    "department": "Engineering & IT",
    "level": "Mid to Senior",
    "experience": "4 - 8 Years",
    "type": "Full-Time / Hybrid",
    "salary": "₹15,00,000 - ₹28,00,000 P.A.",
    "summary": "We are hiring a Site Reliability Engineer to ensure our platform's uptime, performance, and scalability as we grow, owning observability, incident response, and infrastructure automation.",
    "responsibilities": [
      "Design and maintain highly available, scalable infrastructure across cloud environments.",
      "Build and improve monitoring, alerting, and observability tooling across services.",
      "Lead incident response, conduct blameless postmortems, and drive follow-up fixes.",
      "Automate infrastructure provisioning and deployment pipelines using IaC tools.",
      "Define and track SLOs/SLIs for critical services in partnership with engineering teams.",
      "Participate in an on-call rotation to maintain platform reliability."
    ],
    "requirements": [
      "Bachelor's degree in Computer Science or equivalent practical experience.",
      "4+ years of experience in SRE, DevOps, or infrastructure engineering roles.",
      "Strong experience with cloud platforms (AWS/GCP/Azure), Kubernetes, and Terraform.",
      "Proficiency in at least one scripting or programming language (Python, Go, or similar).",
      "Solid understanding of networking, distributed systems, and incident management practices."
    ],
    "preferred": [
      "Experience running production systems at scale for a B2B SaaS company.",
      "Familiarity with observability stacks like Prometheus, Grafana, or Datadog."
    ],
    "benefits": [
      "Competitive compensation with annual performance appraisal and bonus.",
      "Comprehensive group health insurance covering employee and immediate family.",
      "Flexible hybrid work policy with on-call compensation.",
      "Dedicated budget for certifications, conferences, and tooling."
    ],
    "faqs": [
      {
        "q": "What does the on-call rotation look like?",
        "a": "On-call is shared across a team of 6 engineers, roughly one week in six, with clear escalation paths and compensation."
      },
      {
        "q": "What's the current infrastructure stack?",
        "a": "We use Kubernetes, Terraform for infrastructure-as-code, and Datadog for observability."
      }
    ]
  }
];

export function getJobDescriptionBySlug(slug) {
  if (!slug) return null;
  const s = slug.toLowerCase().trim();
  return JOB_DESCRIPTIONS.find(
    (j) => j.slug === s || j.slug === s + "-job-description" || j.slug.replace(/-job-description$/, "") === s
  );
}
